import React from "react";
import { referenceAvailability } from "./referenceAvailability";
import {
  ConfigurationFormActions,
  ConfigurationSyncSummary,
  useConfigurationEditor,
} from "./CustomAiConfigurationForm";
import {
  EVENT_CAPTURE_TIMEFRAMES,
  EVENT_EVALUATION_TIMEFRAMES,
  INSIGHTS_EVENT_TYPES,
} from "../config/insightsEventTypes";

const EVENT_MODES = Object.freeze(["LIGHT", "REFERENCE", "STANDARD"]);
const CODEX_MODELS = Object.freeze([
  "gpt-6-astra",
  "gpt-6-sol",
  "gpt-6-luna",
  "gpt-5.6-sol",
  "gpt-5.6-terra",
  "gpt-5.6-luna",
  "gpt-5.5",
  "gpt-5.4",
  "gpt-5.4-mini",
]);
const REASONING_EFFORTS = Object.freeze(["none", "low", "medium", "high", "xhigh", "max"]);
const PROMPT_CONTROL_CHARACTERS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/;

function SelectField({ label, description, value, options, onChange, disabled = false, unavailable = [] }) {
  const available = options.includes(value) ? options : [value, ...options];
  return (
    <label className="adminFormField">
      <span>{label}</span>
      <select aria-label={label} value={value} disabled={disabled}
        onChange={(event) => onChange(event.target.value)}>
        {available.map((option) => <option key={option} value={option} disabled={unavailable.includes(option)}>
          {option}{unavailable.includes(option) ? " — unavailable" : ""}
        </option>)}
      </select>
      <small>{description}</small>
    </label>
  );
}

function NumberField({ label, description, value, min, max, onChange }) {
  return (
    <label className="adminFormField">
      <span>{label}</span>
      <input type="number" aria-label={label} min={min} max={max} step="1" required value={value}
        onChange={(event) => onChange(Number(event.target.value))} />
      <small>{description}</small>
    </label>
  );
}

function ExecutionFields({ title, value, onChange, reference = false, disabled = false }) {
  const setField = (field, fieldValue) => onChange({ ...value, [field]: fieldValue });
  return (
    <fieldset className="adminFieldset adminFormGrid" disabled={disabled}>
      <legend>{title}</legend>
      <SelectField label={`${title} model`} value={value.model} options={CODEX_MODELS}
        description="Codex model used for this validation profile."
        onChange={(next) => setField("model", next)} />
      <SelectField label={`${title} reasoning effort`} value={value.reasoningEffort} options={REASONING_EFFORTS}
        description="Low is the recommended balance for this small validation."
        onChange={(next) => setField("reasoningEffort", next)} />
      <NumberField label={`${title} timeout in seconds`} value={value.timeoutSeconds} min="30" max="900"
        description="Terminate unfinished validation after 30–900 seconds."
        onChange={(next) => setField("timeoutSeconds", next)} />
      <NumberField label={`${title} validations per UTC day`} value={value.dailyLimit} min="0" max="2147483647"
        description="Safety limit. Use 0 to disable this profile."
        onChange={(next) => setField("dailyLimit", next)} />
      {reference && (
        <>
          <NumberField label="Successful reference threshold" value={value.successThreshold} min="0" max="100"
            description="Minimum similarity score for a successful-pattern match."
            onChange={(next) => setField("successThreshold", next)} />
          <NumberField label="Failed reference threshold" value={value.failureThreshold} min="0" max="100"
            description="Minimum similarity score for a failed-pattern match."
            onChange={(next) => setField("failureThreshold", next)} />
          <NumberField label="Required score edge" value={value.edgeThreshold} min="0" max="100"
            description="Minimum lead over the opposite class before a directional verdict."
            onChange={(next) => setField("edgeThreshold", next)} />
        </>
      )}
    </fieldset>
  );
}

function captureSummary(timeframes) {
  if (timeframes.length === 0) return "No automatic chart capture";
  return timeframes.map((timeframe) => (
    `${timeframe} → ${EVENT_CAPTURE_TIMEFRAMES[timeframe].join(" + ")}`
  )).join(" · ");
}

function policyError(eventType, policy, availability, referenceStatus) {
  if (policy.mode === "STANDARD") {
    return `${eventType}: ${policy.mode} is unavailable for generic event validation. Select LIGHT or turn this policy off.`;
  }
  if (policy.mode !== "OFF" && policy.timeframes.length === 0) {
    return `${eventType} requires at least one timeframe while enabled.`;
  }
  if (policy.mode === "REFERENCE" && referenceStatus !== "ready") {
    return `${eventType}: load reference pack availability before saving REFERENCE mode.`;
  }
  if (policy.mode === "REFERENCE" && !availability.some((entry) => entry.eligible)) {
    return `${eventType}: REFERENCE requires an active complete pack with verified setup images for at least one selected timeframe and direction. Select LIGHT or prepare a pack on the Reference packs tab.`;
  }
  if (policy.promptAppend.length > 500) {
    return `${eventType} guidance must not exceed 500 characters.`;
  }
  if (policy.promptAppend !== policy.promptAppend.trim()) {
    return `${eventType} guidance must not start or end with whitespace.`;
  }
  if (PROMPT_CONTROL_CHARACTERS.test(policy.promptAppend)) {
    return `${eventType} guidance contains unsupported control characters.`;
  }
  return null;
}

function EventPolicyRow({ eventType, label, policy, lastEnabledMode, disabled, onEnabled,
  onMode, onTimeframe, onPrompt, availability, referenceStatus }) {
  const enabled = policy.mode !== "OFF";
  const referenceAvailable = referenceStatus === "ready" && availability.some((entry) => entry.eligible);
  return (
    <section className={`adminEventPolicyRow${enabled ? " adminEventPolicyRowEnabled" : ""}`}>
      <div className="adminEventPolicyIdentity">
        <label className="adminEventPolicySwitch">
          <input type="checkbox" checked={enabled} disabled={disabled}
            aria-label={`Enable ${eventType} validation`} onChange={onEnabled} />
          <span>{label}</span>
        </label>
        <small>{captureSummary(policy.timeframes)}</small>
      </div>
      <SelectField label={`${eventType} validation mode`}
        description="LIGHT validates current evidence. REFERENCE compares with active successful and failed examples; missing packs use LIGHT. Generic STANDARD remains unavailable."
        unavailable={referenceAvailable ? ["STANDARD"] : ["REFERENCE", "STANDARD"]}
        value={enabled ? policy.mode : lastEnabledMode} options={EVENT_MODES} disabled={disabled || !enabled}
        onChange={onMode} />
      <div className="adminEventPolicyTimeframes" role="group" aria-label={`${eventType} processing timeframes`}>
        {EVENT_EVALUATION_TIMEFRAMES.map((timeframe) => (
          <label key={timeframe}>
            <input type="checkbox" checked={policy.timeframes.includes(timeframe)}
              disabled={disabled || !enabled} aria-label={`${eventType} ${timeframe} validation`}
              onChange={() => onTimeframe(timeframe)} />
            <span>{timeframe}</span>
          </label>
        ))}
      </div>
      {enabled && <div className="adminEventPolicyReference" aria-label={`${eventType} reference availability`}>
        {referenceStatus !== "ready" ? <small>{referenceStatus === "loading"
          ? "Loading reference pack availability…" : "Reference pack availability unavailable. Reload packs to enable REFERENCE."}</small>
          : <>
            {availability.map((entry) => <small key={`${entry.timeframe}-${entry.direction}`}>
              {entry.timeframe} {entry.direction}: {entry.eligible
                ? `REFERENCE ready · v${entry.version}` : `${entry.reason} · LIGHT fallback`}
            </small>)}
            {availability.length === 0 && <small>Select a timeframe to see reference availability.</small>}
          </>}
      </div>}
      <details className="adminEventPolicyGuidance">
        <summary>Additional guidance for {eventType}</summary>
        <label>
          <span className="adminVisuallyHidden">{eventType} additional guidance</span>
          <textarea value={policy.promptAppend} maxLength="500" disabled={disabled}
            aria-label={`${eventType} additional guidance`} onChange={(event) => onPrompt(event.target.value)}
            placeholder="Optional evidence emphasis. This cannot override the fixed evaluator rules." />
        </label>
        <small>{policy.promptAppend.length}/500</small>
      </details>
    </section>
  );
}

export default function EventEvaluationConfigurationForm(props) {
  const editor = useConfigurationEditor({
    ...props,
    documentKey: "eventEvaluation",
    label: "event validation",
  });
  const { draft, setDraft } = editor;
  const [referencePacks, setReferencePacks] = React.useState([]);
  const [referenceStatus, setReferenceStatus] = React.useState("loading");
  const [referenceReload, setReferenceReload] = React.useState(0);
  React.useEffect(() => {
    let active = true;
    setReferenceStatus("loading");
    Promise.resolve().then(() => props.api.getReferencePacks()).then((packs) => {
      if (!active) return;
      setReferencePacks(packs);
      setReferenceStatus("ready");
    }).catch(() => { if (active) setReferenceStatus("error"); });
    return () => { active = false; };
  }, [props.api, props.refreshVersion, referenceReload]);
  const [lastEnabledModes, setLastEnabledModes] = React.useState(() => Object.fromEntries(
    INSIGHTS_EVENT_TYPES.map(({ value }) => [
      value,
      draft.eventPolicies[value].mode === "OFF" ? "LIGHT" : draft.eventPolicies[value].mode,
    ]),
  ));
  const policies = draft.eventPolicies;
  const availability = Object.fromEntries(INSIGHTS_EVENT_TYPES.map(({ value }) => [value,
    policies[value].timeframes.flatMap((timeframe) => ["LONG", "SHORT"].map((direction) => ({
      timeframe, direction, ...referenceAvailability(referencePacks, value, timeframe, direction),
    }))),
  ]));
  const hasEligibleReference = referenceStatus === "ready"
    && Object.values(availability).some((entries) => entries.some((entry) => entry.eligible));
  const enabled = Object.values(policies).some((policy) => policy.mode !== "OFF");
  const usesScreenshots = Object.values(policies)
    .some((policy) => ["LIGHT", "REFERENCE"].includes(policy.mode));
  const policyErrors = INSIGHTS_EVENT_TYPES
    .map(({ value }) => policyError(value, policies[value], availability[value], referenceStatus))
    .filter(Boolean);

  const setPolicy = (eventType, nextPolicy) => setDraft({
    ...draft,
    eventPolicies: { ...policies, [eventType]: nextPolicy },
  });
  const setEnabled = (eventType) => {
    const current = policies[eventType];
    if (current.mode === "OFF") {
      setPolicy(eventType, { ...current, mode: "LIGHT" });
      return;
    }
    setLastEnabledModes({ ...lastEnabledModes, [eventType]: current.mode });
    setPolicy(eventType, { ...current, mode: "OFF" });
  };
  const setMode = (eventType, mode) => {
    setLastEnabledModes({ ...lastEnabledModes, [eventType]: mode });
    setPolicy(eventType, { ...policies[eventType], mode });
  };
  const toggleTimeframe = (eventType, timeframe) => {
    const selected = new Set(policies[eventType].timeframes);
    if (selected.has(timeframe)) selected.delete(timeframe);
    else selected.add(timeframe);
    setPolicy(eventType, {
      ...policies[eventType],
      timeframes: EVENT_EVALUATION_TIMEFRAMES.filter((candidate) => selected.has(candidate)),
    });
  };
  const setExecution = (field, value) => setDraft({ ...draft, [field]: value });

  return (
    <form className="adminConfigurationForm" onSubmit={(event) => {
      event.preventDefault();
      if (policyErrors.length === 0) editor.save();
    }}>
      <ConfigurationSyncSummary envelope={editor.envelope} syncStatus={props.syncStatus} enabled={enabled} />
      {usesScreenshots && !props.screenshotsEnabled && (
        <div className="adminAlert adminAlertWarning" role="status">
          Codex chart screenshots are disabled. LIGHT and REFERENCE validations will be skipped until screenshots are enabled on the Codex tab.
        </div>
      )}
      <div className="adminStatusSection">
        {referenceStatus === "error" && <p className="adminAlert adminAlertError" role="alert">
          Could not load reference packs. REFERENCE selection and saving are blocked until availability is known.
        </p>}
        <p className="adminFieldHint">REFERENCE needs an enabled, active pack with six verified setup captures for the event, timeframe and direction. Other occurrences use LIGHT. Manage examples on the Reference packs tab.</p>
        <button type="button" className="adminSecondaryButton" disabled={editor.saving || referenceStatus === "loading"}
          onClick={() => setReferenceReload((value) => value + 1)}>Reload reference availability</button>
        {props.onOpenReferencePacks && <button type="button" className="adminSecondaryButton" disabled={editor.saving}
          onClick={props.onOpenReferencePacks}>Open Reference packs</button>}
      </div>
      <fieldset className="adminFieldset" disabled={editor.saving}>
        <legend>Validation policy by event type</legend>
        <p className="adminFieldHint">Choose which event occurrences are assessed. Guidance is additive and shared by the selected timeframes.</p>
        <div className="adminEventPolicyMatrix">
          {INSIGHTS_EVENT_TYPES.map(({ value, label }) => (
            <EventPolicyRow key={value} eventType={value} label={label} policy={policies[value]}
              lastEnabledMode={lastEnabledModes[value] || "LIGHT"} disabled={editor.saving}
              availability={availability[value]} referenceStatus={referenceStatus}
              onEnabled={() => setEnabled(value)} onMode={(mode) => setMode(value, mode)}
              onTimeframe={(timeframe) => toggleTimeframe(value, timeframe)}
              onPrompt={(promptAppend) => setPolicy(value, { ...policies[value], promptAppend })} />
          ))}
        </div>
      </fieldset>
      <ExecutionFields title="Light validation" value={draft.light}
        disabled={editor.saving}
        onChange={(value) => setExecution("light", value)} />
      <ExecutionFields title="Reference validation" value={draft.reference} reference
        disabled={editor.saving || !hasEligibleReference}
        onChange={(value) => setExecution("reference", value)} />
      {!hasEligibleReference && <p className="adminFieldHint">Reference execution settings unlock when a selected timeframe has an eligible active pack.</p>}
      <p className="adminFieldHint">Light, reference and detailed assessments share the capacity configured on the Workers tab.</p>
      <fieldset className="adminFieldset" disabled>
        <legend>Approved chart mapping</legend>
        <dl className="adminMappingGrid">
          {EVENT_EVALUATION_TIMEFRAMES.map((timeframe) => (
            <div key={timeframe}>
              <dt>{timeframe} event</dt>
              <dd>{draft.timeframeMap[timeframe].join(" + ")}</dd>
            </div>
          ))}
        </dl>
        <p className="adminFieldHint">The current timeframe and one reliable higher timeframe are used. 1D uses only 1D; 1W is intentionally excluded.</p>
      </fieldset>
      <ConfigurationFormActions editor={editor} label="Event validation" onRefresh={props.onRefresh}
        saveDisabled={policyErrors.length > 0} validationMessage={policyErrors[0] || null} />
    </form>
  );
}
